import React, { useState } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../Common/BreadCrumb';
import ConfirmModal from './ConfirmModal';
import { statusLabel, statusBadgeClass } from './changeRequestConstants';
import { formatForDisplay } from '../../utils/dateformatpickr';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Shared list UI for the change-request approval workflow (Reviewal List,
// Approval List, My Changes). All three render the same base columns —
// Created, Created At, Entity Name, Data Key, User Action, Total Changes —
// with an optional Status column and a per-page Action renderer.
//
// Pagination, search and sorting are SERVER-driven (the API is paginated);
// the owning page supplies data/totalRows and the handlers.
//
// Sorting: only fields in the API's sortField whitelist are sortable. Total
// Changes (a detail-array length) and Status/Action are not sortable, per spec.
//
// Expand-to-see-details mirrors PcPpmDataTrackingView's pattern: each row's
// `details` array (Field/Before/After, normalized by useChangeRequests from
// the endpoint-specific detailReviewals/detailApprovals/detailMyList key) is
// shown in an expand panel. `fieldName` may be formatted "Group→Field" for UI
// grouping (per BE note) — split it so the panel shows a clean field label.

const splitFieldName = fieldName => {
  const raw = fieldName || '';
  const parts = raw.split('→');
  return parts.length > 1
    ? { group: parts[0], field: parts.slice(1).join('→') }
    : { group: null, field: raw };
};

const ExpandedFieldChanges = ({ data }) => (
  <div className='p-3 bg-light'>
    <table className='table table-sm mb-0'>
      <thead>
        <tr>
          <th>Field</th>
          <th>Before</th>
          <th>After</th>
        </tr>
      </thead>
      <tbody>
        {(data.details || []).map((change, i) => {
          const { group, field } = splitFieldName(change.fieldName);
          return (
            <tr key={i}>
              <td>{group ? `${group} / ${field}` : field}</td>
              <td>{change.oldValue || '-'}</td>
              <td>{change.newValue || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const tableCustomStyles = {
  header: {
    style: {
      backgroundColor: '#f0f0f0',
      color: '#333',
      fontWeight: 'bold',
    },
  },
  headCells: {
    style: {
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
      fontSize: '14px',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderRight: '1px solid #dee2e6',
      position: 'relative',
      overflow: 'visible',
    },
  },
  cells: {
    style: {
      paddingLeft: '12px',
      paddingRight: '12px',
      wordBreak: 'break-word',
      borderRight: '1px solid #dee2e6',
    },
  },
  rows: {
    style: {
      minHeight: '50px',
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
    },
  },
  table: {
    style: {
      width: '100%',
      minWidth: '100%',
    },
  },
};

const ChangeRequestList = ({
  title,
  pageTitle = 'CRUDE',
  cardTitle,
  documentTitle,
  data = [],
  totalRows = 0,
  loading = false,
  perPage = 10,
  searchValue = '',
  onSearchChange,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
  showStatus = false,
  actionHeader = 'Action',
  renderActions,
  emptyText = 'No data found',
}) => {
  const [confirmState, setConfirmState] = useState(null);

  if (documentTitle) {
    document.title = documentTitle;
  }

  // Passed to each page's renderActions so an action can require confirmation:
  // confirm({ title, message, confirmLabel, confirmColor, showNotes, onConfirm }).
  // onConfirm receives the notes entered in the dialog.
  const confirm = options => setConfirmState(options);
  const closeConfirm = () => setConfirmState(null);
  const handleConfirm = notes => {
    if (confirmState?.onConfirm) confirmState.onConfirm(notes);
    setConfirmState(null);
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      id: 'createdBy',
      name: headerLabel('Created'),
      selector: row => row.createdBy,
      sortable: true,
      wrap: true,
    },
    {
      id: 'created',
      name: headerLabel('Created At'),
      selector: row => formatForDisplay(row.created),
      sortable: true,
      wrap: true,
    },
    {
      id: 'entityName',
      name: headerLabel('Entity Name'),
      selector: row => row.entityName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'dataKey',
      name: headerLabel('Data Key'),
      selector: row => row.dataKey,
      sortable: true,
      wrap: true,
    },
    {
      id: 'changedType',
      name: headerLabel('User Action'),
      selector: row => row.changedType,
      sortable: true,
      wrap: true,
    },
    {
      name: headerLabel('Total Changes'),
      selector: row => row.totalChanges,
      center: true,
      width: '140px',
    },
    ...(showStatus
      ? [
          {
            name: headerLabel('Status'),
            center: true,
            width: '160px',
            cell: row => (
              <span className={statusBadgeClass(row.status)}>
                {statusLabel(row.status)}
              </span>
            ),
          },
        ]
      : []),
    {
      name: headerLabel(actionHeader),
      center: true,
      width: '170px',
      cell: row => renderActions(row, { confirm }),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <ConfirmModal
          show={!!confirmState}
          title={confirmState?.title}
          message={confirmState?.message}
          confirmLabel={confirmState?.confirmLabel}
          confirmColor={confirmState?.confirmColor}
          showNotes={confirmState?.showNotes}
          onConfirm={handleConfirm}
          onClose={closeConfirm}
        />
        <Container fluid>
          <BreadCrumb title={title} pageTitle={pageTitle} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>{cardTitle}</h5>
                    <div className='flex-shrink-0'>
                      <div className='search-box'>
                        <Input
                          type='text'
                          className='form-control form-control-sm'
                          placeholder='Search...'
                          value={searchValue}
                          onChange={e => onSearchChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={data}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={perPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={onChangePage}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      sortServer
                      onSort={onSort}
                      expandableRows
                      expandableRowsComponent={ExpandedFieldChanges}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          {emptyText}
                        </div>
                      }
                      customStyles={tableCustomStyles}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ChangeRequestList;
